import { useCallback, useEffect, useState } from 'react';
import { useBasket } from '~/features/basket';
import { usePDPData } from '~/features/product-details/context';
import { Variant } from '~/lib/data-contract/salesforce';
import { Button, ButtonProps } from '~/shared/components';
import { transformSalesforceProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { getRuntimeConfig } from '~/shared/utils/config';
import { useTranslation } from '~/shared/utils/translation';
import {
    SpiffHostedExperience,
    SpiffHostedExperienceInstance,
    SpiffHostedExperienceResult,
    SpiffProduct,
    SpiffProductInstance,
} from './types';

declare global {
    interface Window {
        Spiff?: {
            Product: SpiffProduct;
            HostedExperience: SpiffHostedExperience;
            IntegrationProduct(): void;
            setRegion(region: string): void;
            getRegion(): string;
        };
    }
}

interface PersonalizationProps extends ButtonProps {
    onComplete?(result: SpiffHostedExperienceResult): void;
}

export const SpiffPersonalization: React.FC<PersonalizationProps> = ({ onComplete, ...rest }) => {
    const { config } = getRuntimeConfig();

    const { translate } = useTranslation();
    const { addItemToBasket, toggleMiniBasket } = useBasket();
    const { product, currency } = usePDPData();

    const { id, inventory } = product || {};

    const [spiffProduct, setSpiffProduct] = useState<SpiffProductInstance>();
    const [hostedExperienceInstance, setHostedExperienceInstance] =
        useState<SpiffHostedExperienceInstance>();

    const addPersonalizedProductToCart = async (result: SpiffHostedExperienceResult) => {
        if (!id) return;

        const payload: Variant = {
            productId: id,
            orderable: inventory?.orderable,
            quantity: 1,
            c_description2: result.transactionId,
            c_personalization_metadata: JSON.stringify(result.designMetaData.metadata),
        };

        const eventData = transformSalesforceProductToDataLayer(product);

        addItemToBasket(eventData, payload, 1)
            .then(() => {
                if (typeof onComplete === 'function') onComplete(result);
            })
            .finally(resetHostedExperience);

        toggleMiniBasket(true);
    };

    const initializeHostedExperience = () => {
        if (typeof window.Spiff === 'undefined') return;

        const hostedExperience = new window.Spiff.HostedExperience({
            applicationKey: config.SPIFF_APPLICATION_KEY,
            product: spiffProduct,
            presentmentCurrency: currency,
        });

        setHostedExperienceInstance(hostedExperience);

        hostedExperience.on('complete', addPersonalizedProductToCart);
        hostedExperience.on('quit', resetHostedExperience);

        return hostedExperience;
    };

    const resetHostedExperience = useCallback(() => {
        // Clear current hosted experience.
        setHostedExperienceInstance(undefined);

        // Removes inline style added by Spiff when opening up workflow.
        if (typeof document !== 'undefined') {
            const body = document.querySelector('body');

            // Add buffer for style changes from Spiff.
            setTimeout(() => {
                if (body) body.style.overflow = '';
            }, 100);
        }
    }, []);

    const onClick = () => {
        // Don't do anything if there's already an existing instance or if there's no Spiff product initialized.
        if (hostedExperienceInstance || !spiffProduct) return;

        const hostedExperience = initializeHostedExperience();

        // Start hosted workflow.
        hostedExperience?.execute();
    };

    // On load, we try and initialize an instance of Spiff's Product API.
    useEffect(() => {
        const initializeSpiffForProduct = async () => {
            const preferredRegion = 'us';

            if (!id || typeof window.Spiff === 'undefined') return null;

            /**
             * Non-US users will be defaulted to use AU servers of Spiff.
             * We will force it to use US servers since our data resides there.
             */
            if (window.Spiff.getRegion() !== preferredRegion) {
                window.Spiff.setRegion(preferredRegion);
            }

            // Clear set Spiff product on consequent triggers.
            setSpiffProduct(undefined);

            const product = new window.Spiff.Product({
                productId: id,
                integrationId: config.SPIFF_INTEGRATION_ID,
            });

            const isValid = await product.confirmActive();

            if (isValid) setSpiffProduct(product);
        };

        // If Spiff is already loaded, start initialization.
        if (typeof window.Spiff !== 'undefined') {
            initializeSpiffForProduct();
        } else {
            // Otherwise, wait for it to be ready.
            window.addEventListener('SpiffApiReady', initializeSpiffForProduct);
        }

        return () => {
            window.removeEventListener('SpiffApiReady', initializeSpiffForProduct);

            // Remove set Spiff instances on cleanup.
            setSpiffProduct(undefined);
            setHostedExperienceInstance(undefined);
        };
    }, [id]);

    return (
        <Button variant="pdp-secondary" disabled={!spiffProduct} {...rest} onClick={onClick}>
            {translate('pdp.personalize')}
        </Button>
    );
};
